<template>
  <teleport to="body">
    <div v-if="modelValue" class="mm-modal-overlay" @click.self="requestClose" @keydown.esc.prevent="requestClose" tabindex="-1">
      <div
        class="mm-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="title || t('aria.modal')"
        ref="dialogRef"
        :style="{ width: maxWidth.includes('px') || maxWidth.includes('%') ? maxWidth : '', '--mm-modal-max-width': !maxWidth.includes('px') && !maxWidth.includes('%') ? maxWidth : '' }"
        :class="[!maxWidth.includes('px') && !maxWidth.includes('%') ? maxWidth : '']"
      >
        <header class="mm-modal__header">
          <h2 class="mm-modal__title">{{ title }}</h2>
          <button type="button" class="mm-modal__close" @click="requestClose" :aria-label="t('buttons.close')">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 1 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.9a1 1 0 0 0 1.41-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4z"/></svg>
          </button>
        </header>
        <section class="mm-modal__body">
          <slot />
        </section>
        <footer class="mm-modal__footer">
          <slot name="footer">
            <button type="button" class="mm-modal__button" @click="requestClose">{{ t('buttons.closeWindow') }}</button>
          </slot>
        </footer>
      </div>
    </div>
  </teleport>
  
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue'
import { t } from '../i18n'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: '' },
  maxWidth: { type: String, default: '560px' },
})

const emit = defineEmits(['update:modelValue'])
const dialogRef = ref(null)

function requestClose() {
  emit('update:modelValue', false)
}

function handleFocusTrap(e) {
  if (!props.modelValue) return
  if (e.key !== 'Tab') return
  const root = dialogRef.value
  if (!root) return
  const focusable = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  if (!focusable.length) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault()
    first.focus()
  }
}

watch(() => props.modelValue, async (open) => {
  if (open) {
    // lock scroll
    document.documentElement.style.overflow = 'hidden'
    await nextTick()
    // focus close button
    const closeBtn = dialogRef.value?.querySelector('.mm-modal__close')
    closeBtn?.focus()
    window.addEventListener('keydown', handleFocusTrap)
  } else {
    document.documentElement.style.overflow = ''
    window.removeEventListener('keydown', handleFocusTrap)
  }
})

onBeforeUnmount(() => {
  document.documentElement.style.overflow = ''
  window.removeEventListener('keydown', handleFocusTrap)
})
</script>

<style scoped>
.mm-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: grid;
  place-items: center;
  z-index: 9999;
}

.mm-modal {
  width: var(--mm-modal-max-width, 560px); 
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  background: #fff;
  border: 1px solid #000;
  border-radius: 12px; /* afgeronde hoeken */
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  display: flex;
  flex-direction: column;
  /* Laat inhoud scrollen op containerniveau zodat dropdowns van selects niet in een inner wrapper worden afgekapt */
  overflow: auto;
  position: relative;
}

.mm-modal__header {
  background: #EEDCC5;
  color: #000;
  padding: 16px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #000;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.mm-modal__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.mm-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  border: 1px solid #000;
  background: #fff;
  color: #000;
  border-radius: 8px;
  cursor: pointer;
}

.mm-modal__body {
  padding: 16px;
  color: #000;
  background: #fff;
  /* Belangrijk: geen overflow clipping hier, anders worden native select opties afgesneden */
  overflow: visible;
}

/* Verbeter zichtbaarheid van formulier-velden binnen de modal
   We gebruiken :deep zodat slotted content (uit andere componenten) ook gestyled wordt. */
.mm-modal__body :deep(input[type="text"],
                     input[type="email"],
                     input[type="number"],
                     input[type="url"],
                     input[type="tel"],
                     input[type="search"],
                     select,
                     textarea) {
  background: #fff !important;
  color: #000 !important;
  border: 1px solid #000 !important; /* forceer zwarte rand bovenop utility classes */
  border-radius: 0px !important;
  padding: 10px 12px !important;
  font-size: 15px !important;
  line-height: 1.4 !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

.mm-modal__body :deep(textarea) {
  min-height: 96px; /* iets royaler standaard-veld in modals */
}

/* Uitzondering: voor de specifieke vrijgave-reden textarea (#release-reason)
   moeten we de Tailwind utility-styles exact respecteren zoals gevraagd:
   w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-[15px] */
.mm-modal__body :deep(textarea#release-reason) {
  /* border-neutral-300 */
  border: 1px solid #d4d4d4 !important;
  /* rounded-md (6px) */
  border-radius: 6px !important;
  /* px-3 py-2 (12px x 8px) */
  padding: 8px 12px !important;
  /* bg-white en text-[15px] komen al overeen met de generieke regels */
}

/* Labels duidelijk als blok zodat ze niet over velden heen vallen */
.mm-modal__body :deep(label) {
  display: block;
  margin-bottom: 4px;
}

.mm-modal__body :deep(input::placeholder,
                      textarea::placeholder) {
  color: #6b7280; /* neutrale placeholder kleur */
}

.mm-modal__body :deep(input:focus,
                     select:focus,
                     textarea:focus) {
  outline: 2px solid #000 !important;
  outline-offset: 2px !important;
}

/* Zorg dat opties in select lijsten ook goed contrasteren (browser-ondersteuning kan variëren) */
.mm-modal__body :deep(select) {
  /* Zorg dat het vanaf stilstand duidelijk is dat dit een dropdown is */
  -webkit-appearance: none !important; /* eigen pijl tonen i.p.v. platform */
  appearance: none !important;
  background-color: #fff !important;
  /* Duidelijke pijl rechts binnen het veld */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 20 20'%3E%3Cpath d='M5.5 7.5l4.5 4.5 4.5-4.5' stroke='%23000' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: right 10px center !important;
  background-size: 14px 14px !important;
  padding-right: 34px !important; /* ruimte voor het pijltje */
  /* Forceer hetzelfde kader als inputs/textarea */
  border: 1px solid #000 !important;
  border-radius: 8px !important;
}
.mm-modal__body :deep(select option) {
  color: #000;
  background: #fff;
}

/* Maak buttons binnen de modal-contents duidelijk en consistent zichtbaar */
.mm-modal__body :deep(button) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fff;
  color: #000;
  border: 1px solid #000;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.mm-modal__body :deep(button:hover) {
  background: #000;
  color: #fff;
}

.mm-modal__body :deep(button:focus) {
  outline: 2px solid #000;
  outline-offset: 2px;
}

.mm-modal__body :deep(button[disabled]),
.mm-modal__body :deep(button:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
  color: #666;
  border-color: #000;
}

/* Visuele uitgrijzing voor disabled invoervelden in de modal */
.mm-modal__body :deep(input[disabled],
                     select[disabled],
                     textarea[disabled]) {
  background: #f3f4f6 !important; /* lichtgrijs */
  color: #6b7280 !important;      /* gedimd */
  border-color: #d1d5db !important; /* grijs kader */
}

.mm-modal__footer {
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid #000;
  background: #fff;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

/* Style voor knoppen die vanuit slots worden doorgegeven */
:slotted(.mm-modal__button) {
  padding: 10px 14px;
  background: #fff;
  color: #000;
  border: 1px solid #000;
  border-radius: 8px; /* vierkante button met lichte afronding passend bij stijl */
  cursor: pointer;
}

:slotted(.mm-modal__button:focus),
.mm-modal__close:focus {
  outline: 2px solid #000;
  outline-offset: 2px;
}
</style>
